import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { ArrowLeft, Camera, Shield, Monitor, Wifi, Bell, HardDrive, Wrench, CheckCircle, Eye, Network, Smartphone } from 'lucide-react';
import VideoBackground from './VideoBackground';

const SurveillancePage = () => {
  const navigate = useNavigate();

  const surveillanceVideoUrl = "https://customer-assets.emergentagent.com/job_corporate-connect-18/artifacts/l1yxq6gj_video_20260331_184544.webm";
  const fallbackImage = "https://images.pexels.com/photos/430208/pexels-photo-430208.jpeg";

  const deploymentServices = [
    {
      id: 1,
      title: "Site Survey & Security Assessment",
      description: "Comprehensive analysis of your property to identify security vulnerabilities and optimal camera placement locations.",
      icon: Eye
    },
    {
      id: 2,
      title: "Camera Placement Planning & Installation",
      description: "Strategic positioning of CCTV and IP cameras for maximum coverage and clarity.",
      icon: Camera
    },
    {
      id: 3,
      title: "DVR / NVR Configuration",
      description: "Professional setup and configuration of Digital and Network Video Recorders for seamless recording.",
      icon: Monitor
    },
    {
      id: 4,
      title: "IP Camera & Network Setup",
      description: "Complete network integration of IP surveillance systems with secure connectivity.",
      icon: Network
    },
    {
      id: 5,
      title: "Remote Mobile Viewing Integration",
      description: "Enable 24/7 access to your surveillance feeds from anywhere via mobile devices.",
      icon: Smartphone
    },
    {
      id: 6,
      title: "Motion Detection & Alert Configuration",
      description: "Smart alert systems that notify you instantly of any suspicious activity.",
      icon: Bell
    },
    {
      id: 7,
      title: "Storage & Backup Setup",
      description: "Reliable storage solutions with automated backup for continuous footage retention.",
      icon: HardDrive
    },
    {
      id: 8,
      title: "Preventive Maintenance & AMC Support",
      description: "Annual maintenance contracts ensuring your surveillance system operates flawlessly.",
      icon: Wrench
    }
  ];

  const keyFeatures = [
    "CCTV Camera Installation",
    "IP Surveillance Systems",
    "NVR/DVR Units",
    "Access Control Devices",
    "Remote Monitoring Solutions",
    "Cloud Access Setup",
    "24/7 System Monitoring",
    "Scalable Architecture"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section with Video Background */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20 px-4 overflow-hidden">
        {/* Video Background */}
        <VideoBackground 
          videoUrl={surveillanceVideoUrl} 
          fallbackImage={fallbackImage}
        />
        
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 via-slate-900/70 to-slate-900/60 z-0"></div>

        {/* Animated Background Pattern */}
        <div className="absolute inset-0 opacity-5 z-0">
          <div 
            className="h-full w-full"
            style={{
              backgroundImage: `
                linear-gradient(rgba(16, 185, 129, 0.3) 1px, transparent 1px),
                linear-gradient(90deg, rgba(16, 185, 129, 0.3) 1px, transparent 1px)
              `,
              backgroundSize: '50px 50px',
              animation: 'grid-scroll 20s linear infinite'
            }}
          />
        </div>

        <div className="container mx-auto relative z-10">
          {/* Back Button */}
          <Button
            onClick={() => navigate('/')}
            variant="outline"
            className="mb-8 border-white/30 text-white hover:bg-white/10 backdrop-blur-sm"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>

          {/* Title Section */}
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/50">
                <Camera className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-4xl sm:text-5xl font-bold text-white drop-shadow-lg">
                  AI Surveillance Services
                </h1>
                <p className="text-emerald-400 text-xl font-semibold mt-1">Professional Deployment</p>
              </div>
            </div>
            
            <p className="text-xl text-gray-200 leading-relaxed mb-6 drop-shadow-md backdrop-blur-sm bg-black/20 p-4 rounded-lg">
              We provide end-to-end surveillance system deployment services designed to ensure complete security, visibility, and operational control for residential, commercial, and industrial environments.
            </p>

            <p className="text-lg text-gray-300 leading-relaxed backdrop-blur-sm bg-black/20 p-4 rounded-lg">
              Our deployment services include site assessment, system design, device installation, network integration, configuration, testing, and ongoing maintenance support. We specialize in the installation of CCTV cameras, IP surveillance systems, NVR/DVR units, access control devices, and remote monitoring solutions.
            </p>
          </div>
        </div>
      </div>

      {/* Key Features Section */}
      <div className="py-16 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              What We Specialize In
            </h2>
            <div className="grid md:grid-cols-4 gap-4">
              {keyFeatures.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 bg-gradient-to-r from-emerald-50 to-cyan-50 rounded-lg p-4 hover:shadow-md transition-all duration-300 card-hover"
                >
                  <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0" />
                  <span className="text-gray-800 font-medium">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Deployment Services Section */}
      <div className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Our Deployment Services Include
            </h2>
            <p className="text-xl text-gray-600">
              From strategic camera placement to seamless mobile and cloud access setup, our team ensures every surveillance solution is deployed with precision, reliability, and scalability.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {deploymentServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card 
                  key={service.id}
                  className="border-2 hover:border-emerald-500/50 transition-all duration-300 hover:shadow-xl card-hover animate-slide-in-bottom"
                  style={{ animationDelay: `${index * 50}ms` }}
                >
                  <CardContent className="pt-6">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-lg flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/30">
                      <Icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-3">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {service.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Why Choose Us Section */}
      <div className="py-20 bg-gradient-to-br from-slate-900 to-slate-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <Shield className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-6">
              Professional Surveillance Solutions
            </h2>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              We deliver professional surveillance solutions tailored to your security requirements, helping protect your assets, people, and operations 24/7.
            </p>
            
            <div className="grid md:grid-cols-3 gap-6 mt-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <Camera className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Residential Security</h3>
                <p className="text-gray-400">Comprehensive home surveillance systems</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <Monitor className="w-10 h-10 text-cyan-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Commercial Solutions</h3>
                <p className="text-gray-400">Business-grade monitoring systems</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <Wifi className="w-10 h-10 text-emerald-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Industrial Control</h3>
                <p className="text-gray-400">Large-scale facility monitoring</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Ready to Secure Your Property?
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Get a free consultation and site assessment from our surveillance experts.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => navigate('/', { state: { scrollTo: 'contact' } })}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-cyan-600 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg shadow-emerald-500/30"
              >
                Request Free Consultation
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => navigate('/')}
                className="border-2 border-gray-300 hover:border-emerald-500 px-8 py-6 text-lg font-semibold rounded-lg"
              >
                View All Services
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SurveillancePage;
